// components/groupAdmin/feed/list/FeedList.jsx
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link as LinkIcon, FileText, Pencil, X, GraduationCap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function FeedList({
  loading,
  items,
  onRemove,
  onEdit,
  onEditCourse,
}) {
  const { t } = useTranslation();

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>{t('groupAdminTabs.feed.feedListTitle', { defaultValue: 'Feed items' })}</CardTitle>
        <CardDescription>{t('groupAdminTabs.feed.feedListDescription', { defaultValue: 'Manage what appears in your group feed.' })}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
        {loading ? (
          <p className="text-slate-500 text-sm">{t('groupAdminTabs.feed.loading', { defaultValue: 'Loading…' })}</p>
        ) : !items?.length ? (
          <p className="text-slate-500 text-sm">{t('groupAdminTabs.feed.noItems', { defaultValue: 'No items yet.' })}</p>
        ) : (
          items.map((item) => {
            const type = item.type || (item.course_id || item.content_ref?.collection === 'courses' ? 'course' : 'article');
            const hasPoll = !!item.poll;
            const hasExternal = !!(item.article_url || item.external_url);
            const hasAttachments = Array.isArray(item.attachments) && item.attachments.length > 0;

            return (
              <Card key={item.id} className="border border-slate-200">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{item.title}</CardTitle>
                      <CardDescription className="text-xs mt-1">
                        {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                      </CardDescription>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {type === 'course' && (
                        <Badge variant="outline">
                          <span className="inline-flex items-center gap-1">
                            <GraduationCap size={14} />
                            {t('groupAdminTabs.feed.badge.course', { defaultValue: 'Course' })}
                          </span>
                        </Badge>
                      )}

                      {hasPoll && <Badge variant="outline">{t('groupAdminTabs.feed.badge.poll', { defaultValue: 'Poll' })}</Badge>}
                      {hasExternal && <Badge variant="outline">{t('groupAdminTabs.feed.badge.article', { defaultValue: 'Article' })}</Badge>}
                      {hasAttachments && (
                        <Badge variant="outline">
                          {item.attachments.length} {t('groupAdminTabs.feed.badge.files', { defaultValue: 'files' })}
                        </Badge>
                      )}
                      {item.is_pinned && <Badge variant="secondary">{t('groupAdminTabs.feed.badge.pinned', { defaultValue: 'Pinned' })}</Badge>}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {item.image_url && (
                    <img src={item.image_url} alt={item.title} className="w-full max-h-60 object-cover rounded-lg" />
                  )}

                  {item.description && (
                    <p className="text-sm text-slate-700 whitespace-pre-line">
                      {item.description ?? item.body}
                    </p>
                  )}

                  {type === 'course' && item.course_preview && (
                    <div className="text-xs text-slate-600">
                      {t('groupAdminTabs.feed.coursePreview', {
                        defaultValue: '{{m}} modules · {{l}} lessons · {{s}}',
                        m: item.course_preview.module_count ?? 0,
                        l: item.course_preview.lesson_count ?? 0,
                        s: item.course_preview.status || 'draft',
                      })}
                    </div>
                  )}

                  {hasExternal && (
                    <a
                      href={item.article_url || item.external_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center text-sm text-blue-600 hover:underline"
                    >
                      <LinkIcon size={14} className="mr-1" />
                      {t('groupAdminTabs.feed.openArticle', { defaultValue: 'Open article' })}
                    </a>
                  )}

                  {hasPoll && (
                    <div className="border rounded-lg p-3">
                      <p className="text-sm font-medium mb-2">{item.poll.question}</p>
                      <ul className="space-y-1">
                        {(item.poll.options || []).map((opt) => (
                          <li key={opt.id || opt.option || opt} className="flex items-center justify-between text-sm">
                            <span>{opt.text || opt.option || opt}</span>
                            {typeof opt.votes === 'number' && (
                              <span className="text-xs text-slate-500 ml-2">
                                {opt.votes} vote{opt.votes === 1 ? '' : 's'}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!!item.attachments?.length && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-slate-500">{t('groupAdminTabs.feed.attachments', { defaultValue: 'Attachments' })}</p>
                      <ul className="space-y-1">
                        {item.attachments.map((att) => (
                          <li key={att.id || att.url}>
                            <a
                              href={att.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-xs text-blue-600 hover:underline"
                            >
                              <FileText size={12} className="mr-1" />
                              {att.name || 'Download file'}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="pt-1 flex justify-end gap-2 flex-wrap">
                    <Button variant="ghost" size="sm" onClick={() => onRemove?.(item.id)}>
                      <X size={14} className="mr-1" />
                      {t('groupAdminTabs.feed.remove', { defaultValue: 'Remove' })}
                    </Button>

                    <Button size="sm" variant="outline" onClick={() => onEdit?.(item)}>
                      <Pencil size={14} className="mr-1" />
                      {t('groupAdminTabs.feed.edit', { defaultValue: 'Edit' })}
                    </Button>

                    {type === 'course' && (
                      <Button size="sm" onClick={() => onEditCourse?.(item)}>
                        <GraduationCap size={14} className="mr-1" />
                        {t('groupAdminTabs.feed.manageCourse', { defaultValue: 'Manage course' })}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
